import React, { ReactElement, ReactNode } from "react";
import clsx from "clsx";
import Loader from "../Loader";

export interface IButtonProps {
    type?: "button" | "submit" | "reset";
    label?: string;
    loading?: boolean;
    disabled?: boolean;
    bgColor?: "primary" | "bg-background" | "secondary" | "bg-primary-border"
    onClick?: () => void | Promise<void> | undefined;
    className?: string,
    labelClasses?: string,
    children?: string | JSX.Element | ReactElement | ReactNode | null;
    icon?: string | JSX.Element | ReactElement | ReactNode | null;
    title?: string;
    fullWidth?: boolean;
    curved?: boolean;
    size?: "lg" | "md" | "sm",
    id?: string;
    style?: React.CSSProperties;
    RoundedFull?: boolean;
    prependIcon?: string | JSX.Element | ReactElement | ReactNode | null;
    appendIcon?: string | JSX.Element | ReactElement | ReactNode | null;
}

function Button({
    type = "button",
    label,
    loading = false,
    disabled = false,
    onClick,
    bgColor,
    className,
    labelClasses,
    children,
    icon,
    title,
    fullWidth = false,
    curved = true,
    size = "md",
    id,
    style,
    prependIcon,
    appendIcon
}: IButtonProps): JSX.Element {
    const buttonLabel = children ? children : label;
    
    // Memoized color logic to prevent recalculations
    const getButtonStyles = () => {
        const isDisabled = disabled;
        const hasYellowBg = className?.includes('bg-yellow') || className?.includes('bg-amber') || className?.includes('bg-orange') || className?.includes('bg-primary-main');
        
        // Base styles
        const baseStyles = "text-base font-medium  shadow-sm flex items-center focus:ring-primary-border w-fit h-[39px] px-4";
        
        // Size styles
        const sizeStyles = {
            "w-full": fullWidth,
            "rounded-full": curved,
            "p-3": size === "md",
            "p-2 text-sm": size === "sm"
        };
        
        // Color styles
        let colorStyles = "";
        
        if (isDisabled) {
            colorStyles = "bg-gray-300 dark:bg-darkBg-border cursor-not-allowed text-gray-500 dark:text-gray-600";
        } else if (hasYellowBg) {
            colorStyles = "text-black hover:text-black dark:text-black dark:hover:text-black";
        } else if (bgColor === "primary") {
            colorStyles = "bg-primary-main text-black border-none hover:bg-primary-main/90 dark:hover:bg-primary-main/80";
        } else if (bgColor === "secondary") {
            colorStyles = "bg-secondary-main dark:bg-darkBg border text-secondary-foreground dark:text-darkBg-mainText border-secondary-border dark:border-darkBg-border hover:bg-secondary-main/90 dark:hover:bg-darkBg-border/80";
        } else if (bgColor === "bg-background") {
            colorStyles = "bg-background dark:bg-darkBg text-foreground dark:text-darkBg-mainText border-border dark:border-darkBg-border hover:bg-background/90 dark:hover:bg-darkBg/80";
        } else if (bgColor === "bg-primary-border") {
            colorStyles = "bg-transparent dark:bg-transparent text-primary-main dark:text-primary-main border-primary-main dark:border-primary-main hover:bg-primary-main/10 dark:hover:bg-primary-main/20";
        } else {
            colorStyles = "bg-white dark:bg-gray-300 text-gray-700 dark:text-black border-gray-300 dark:border dark:border-darkBg-border hover:bg-gray-50 ";
        }
        
        return clsx(baseStyles, colorStyles, sizeStyles, className);
    };
    
    // Memoized icon color logic
    const getIconColor = () => {
        const isDisabled = disabled;
        const hasYellowBg = className?.includes('bg-yellow') || className?.includes('bg-amber') || className?.includes('bg-orange');
        
        if (isDisabled) return "text-gray-400 dark:text-gray-500";
        if (hasYellowBg) return "text-black hover:text-black dark:text-black dark:hover:text-black";
        if (bgColor === "primary") return "text-black";
        if (bgColor === "secondary") return "text-secondary-foreground dark:text-darkBg-subText";
        if (bgColor === "bg-background") return "text-foreground dark:text-darkBg-subText";
        if (bgColor === "bg-primary-border") return "text-primary-main dark:text-primary-main";
        return "text-gray-500 dark:text-gray-400";
    };
    
    const iconColor = getIconColor();
    
    return (
        <button
            className={getButtonStyles()}
            type={type}
            disabled={disabled}
            onClick={onClick}
            title={title}
            id={id}
            style={style}
        >
            {(icon || prependIcon) && !loading && (
                <span className={`flex-none mr-2 ${iconColor}`}>
                    {icon || prependIcon}
                </span>
            )}
            <span className={clsx(`flex justify-center whitespace-nowrap w-full items-center`, labelClasses)}>
                {loading ? <Loader size="xs" /> : buttonLabel}
            </span>
            {appendIcon && !loading && (
                <span className={`flex-none ml-2 ${iconColor}`}>
                    {appendIcon}
                </span>
            )}
        </button>
    );
}

export default Button;